// LinksPopover.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  FloatingPortal,
} from '@floating-ui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';

const LinksPopover = ({ onetSOC, userDefaults, linkBLS }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { x, y, strategy, refs } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
  });

  // Toggle the popover open state
  const togglePopover = () => {
    setIsOpen((prev) => !prev);
  };

  // Handler to close the popover when clicking outside
  const handleClickOutside = useCallback(
    (event) => {
      const floatingElement = refs.floating.current;
      const referenceElement = refs.reference.current;

      if (
        floatingElement &&
        !floatingElement.contains(event.target) &&
        referenceElement &&
        !referenceElement.contains(event.target)
      ) {
        setIsOpen(false);
      }
    },
    [refs]
  );

  // Handler to close the popover on Escape key press
  const handleEscape = useCallback((event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  // Manage event listeners based on popover state
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    }

    // Cleanup on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClickOutside, handleEscape]);

  return (
    <div className="print:hidden whitespace-nowrap text-blue-500 flex items-center justify-center">
      {/* Popover Button */}
      <button
        ref={refs.setReference} // Correctly assign the reference ref
        onClick={togglePopover}
        className="inline-flex justify-center rounded-md hover:text-blue-500 p-1 text-sm font-medium text-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
        </svg>
        <ChevronDownIcon
          className="ml-1 -mr-1 h-3 w-3 text-blue-500"
          aria-hidden="true"
        />
      </button>

      {/* Popover Panel with Framer Motion Animations */}
      <FloatingPortal>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={refs.setFloating} // Correctly assign the floating ref
              className="z-50 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <div className="py-1">
                {/* CareerOneStop Profile */}
                <a
                  href={`https://www.careeronestop.org/toolkit/careers/occupations/Occupation-profile.aspx?keyword=${onetSOC
                    .toString()
                    .replace(/\B(?=(\d{4})+(?!\d))/g, '-')}.00&location=WA`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-500 hover:text-white rounded-md"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="#bae6fd"
                  >
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  CareerOneStop Profile
                </a>

                {/* Indeed Job Board */}
                <a
                  href={`https://www.careeronestop.org/Toolkit/Jobs/find-jobs-results.aspx?keyword=${onetSOC
                    .toString()
                    .replace(/\B(?=(\d{4})+(?!\d))/g, '-')}.00&location=${userDefaults.defaultZip}&radius=${userDefaults.defaultRadius}&source=NLX`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-500 hover:text-white rounded-md"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="#bae6fd"
                  >
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  NLx Job Board
                </a>

                {/* O*NET Profile */}
                <a
                  href={`https://www.onetonline.org/link/summary/${onetSOC
                    .toString()
                    .replace(/\B(?=(\d{4})+(?!\d))/g, '-')}.00`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-500 hover:text-white rounded-md"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="#bae6fd"
                  >
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  O*NET Profile
                </a>

{/* Career Bridge */}
<a
  href={`https://www.careerbridge.wa.gov/Detail_Occupation.aspx?soc=${onetSOC.replace(/-/g, '')}`}
  target="_blank"
  rel="noopener noreferrer"
  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-500 hover:text-white rounded-md"
>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="#bae6fd"
                  >
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  Career Bridge
                </a>

                {/* BLS Handbook */}
                <a
                  href={linkBLS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-500 hover:text-white rounded-md"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="#bae6fd"
                  >
                    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                  </svg>
                  BLS Handbook
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};

export default LinksPopover;